/**
 * 화면 가리키기 — 도움말·Quick Start·AI 에이전트가 **손가락**으로 화면의 한 곳을 가리킨다.
 *
 * 채팅 글만으로는 "툴박스 데이터 › SAM파일" 이 어디인지 찾기 어렵다. 손가락이 그곳으로 날아가 톡톡 치고,
 * 대상 둘레가 빛나며 말풍선이 붙는다. 그리는 쪽은 components/common/PointerOverlay.tsx 하나다.
 *
 * 대상(target)은 문자열 키다:
 *   · `toolbox:<노드 유형>`   툴박스 항목 (csvData, rdbData, transformLoad, filter …)
 *   · `toolbar:<동작>`        캔버스 오른쪽 위 도구 모음 (undo, redo, save, versions, history, code, deploy, run, stop,
 *                             export, import, image, pdf)
 *   · `header:<버튼>`         위쪽 시스템 버튼 (project, dataDef, vars, conns, connLimits, templates, bulk, explorer, ai, help)
 *   · `panel:<영역>`          props(노드 속성), explorer(워크플로 탐색기), ai(AI 에이전트 창)
 *   · `node:<id 또는 라벨>`   캔버스 노드 — 화면 밖이면 가운데로 옮긴 뒤 가리킨다
 * 화면 요소에는 `data-point="<키>"` 를 붙여 둔다(노드는 React Flow 의 data-id 로 찾는다).
 */

export const POINT_EVENT = 'openflow:point-at';

export interface PointOptions {
  /** 말풍선 글 */
  label?: string;
  /** 도착한 뒤 누르는 동작을 한 번 보여 준다 (「대신 해줘」가 그 버튼을 누르는 것처럼) */
  tap?: boolean;
  /** 몇 ms 뒤 사라질지 — 0 이면 다른 가리키기·clearPointer 까지 남는다. 기본 6000 */
  hold?: number;
  /** 날아가는 시간(ms) — 기본 550. 먼 곳(툴박스 → 캔버스 노드)은 길게 주어 눈으로 따라올 수 있게 */
  flyMs?: number;
}

export interface PointRequest extends PointOptions {
  /** 빈 문자열이면 지운다 */
  target: string;
  /** 도착(과 누르기)이 끝나면 부른다 — 찾지 못했으면 false */
  done?: (found: boolean) => void;
}

/** 대상 키 목록 — AI 도구 설명·검사에 쓴다 */
export const POINT_TARGET_HELP =
  'toolbox:<nodeType> | toolbar:undo|redo|save|versions|history|code|deploy|run|stop|export|import|image|pdf | '
  + 'header:project|dataDef|vars|conns|connLimits|templates|bulk|explorer|ai|help | panel:props|explorer|ai | node:<label or id>';

const TARGET_RE = /^(toolbox|toolbar|header|panel|node):.+/;
export const isPointTarget = (t: string) => TARGET_RE.test(String(t || '').trim());

/** 손가락을 보낸다. 도착(tap 이면 누르기까지) 뒤에 풀린다 — 가리킬 화면이 없으면(오버레이 없음) 잠시 뒤 false */
export function pointAt(target: string, opts: PointOptions = {}): Promise<boolean> {
  return new Promise((resolve) => {
    let settled = false;
    const done = (found: boolean) => { if (!settled) { settled = true; resolve(found); } };
    window.dispatchEvent(new CustomEvent<PointRequest>(POINT_EVENT, { detail: { ...opts, target, done } }));
    window.setTimeout(() => done(false), 4000);
  });
}

/**
 * 캔버스 이동(centerOnNode, WorkflowCanvas 의 setCenter 500ms)이 끝날 때까지 기다린다 —
 * 움직이는 노드를 손가락이 매 프레임 쫓으면 급하게 끌려가는 것처럼 보인다.
 */
export const CANVAS_PAN_MS = 650;
export const waitPan = () => new Promise<void>((r) => window.setTimeout(r, CANVAS_PAN_MS));

export function clearPointer(): void {
  window.dispatchEvent(new CustomEvent<PointRequest>(POINT_EVENT, { detail: { target: '' } }));
}
